import { useState } from 'react'
import { useForm } from 'react-hook-form'
import isError from 'lodash/isError'
import { BarChart3, FileText, FolderOpen, type LucideIcon } from 'lucide-react'
import { canSendSourceDocuments } from '@klear/constants/document-processing'
import type { DocumentDestination } from '@klear/schema/documents'
import {
  documentFormFieldErrors,
  documentFormPeriodEnd,
  hasDocumentFormErrors,
  validateDocumentForm,
  type DocumentForm
} from '@klear/document/wire.client'
import { failedUploadsMessage, type UploadFileStatus } from '@klear/ui/hooks/use-document-upload-request'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@klear/ui/components/dialog'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { toast } from '@klear/ui/hooks/use-toast'
import { trpc } from '@klear/ui/lib/trpc'
import TraderDocumentForm from '@/app/document/document-form-common'
import { uploadDocument } from '@klear/ui/lib/upload-document'
import { OrderDocumentUpload } from '@/app/document/order-document-upload'
import { UploadStep } from '@/app/document/upload-step'

type UploadKind = 'order' | 'financial' | 'other'

type UploadDocumentDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  traderId: number
}

const STEP_COPY: Record<UploadKind, { title: string; description: string }> = {
  order: {
    title: 'Upload order documents',
    description: 'Klear reads each file and files it on the right order.'
  },
  financial: {
    title: 'Upload a financial statement',
    description: 'Tag the quarter its period ends in so Klear knows how current your financials are.'
  },
  other: {
    title: 'Upload a document',
    description: 'Keep it with your documents. Klear does not review it.'
  }
}

export function UploadDocumentDialog({ open, onOpenChange, traderId }: UploadDocumentDialogProps) {
  const [kind, setKind] = useState<UploadKind | null>(null)
  const [busy, setBusy] = useState(false)
  const { user } = useAuth()

  const close = () => {
    setKind(null)
    onOpenChange(false)
  }
  const back = () => setKind(null)

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : !busy && close())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{kind === null ? 'Upload Document' : STEP_COPY[kind].title}</DialogTitle>
          <DialogDescription>
            {kind === null ? 'What are you uploading?' : STEP_COPY[kind].description}
          </DialogDescription>
        </DialogHeader>
        {kind === null ? (
          <div className="grid gap-3">
            {canSendSourceDocuments(user, traderId, null) && (
              <KindCard
                icon={FileText}
                title="Order document"
                description="Invoices, purchase orders and proofs of acceptance."
                onClick={() => setKind('order')}
              />
            )}
            <KindCard
              icon={BarChart3}
              title="Financial document"
              description="P&L, balance sheet or other financial statements."
              onClick={() => setKind('financial')}
            />
            <KindCard
              icon={FolderOpen}
              title="Other"
              description="W-9, certificate of insurance, bank letter or anything else."
              onClick={() => setKind('other')}
            />
          </div>
        ) : (
          <UploadStep onBack={back} busy={busy}>
            {kind === 'order' ? (
              <OrderDocumentUpload traderId={traderId} busy={busy} onBusyChange={setBusy} onDone={close} />
            ) : (
              <StoredDocumentUpload
                traderId={traderId}
                isFinancialStatement={kind === 'financial'}
                busy={busy}
                onBusyChange={setBusy}
                onDone={close}
              />
            )}
          </UploadStep>
        )}
      </DialogContent>
    </Dialog>
  )
}

function KindCard({
  icon: Icon,
  title,
  description,
  onClick
}: {
  icon: LucideIcon
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
      <div className="space-y-1">
        <div className="text-sm font-medium">{title}</div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </button>
  )
}

function destinationOf(data: DocumentForm): DocumentDestination {
  const periodEnd = data.financialStatement === true ? documentFormPeriodEnd(data) : null
  return periodEnd === null
    ? { kind: 'documents_page' }
    : { kind: 'financial_statement', periodEnd: periodEnd.toPeriodEnd() }
}

function StoredDocumentUpload({
  traderId,
  isFinancialStatement,
  busy,
  onBusyChange,
  onDone
}: {
  traderId: number
  isFinancialStatement: boolean
  busy: boolean
  onBusyChange: (busy: boolean) => void
  onDone: () => void
}) {
  const utils = trpc.useUtils()

  const form = useForm<DocumentForm>({
    defaultValues: { title: '', notes: '', financialStatement: isFinancialStatement },
    resolver: async (values) => {
      const errors = validateDocumentForm(values, { requireFile: true })
      if (hasDocumentFormErrors(errors)) {
        return { values: {}, errors: documentFormFieldErrors(errors) }
      }
      return { values, errors: {} }
    }
  })

  const onSubmit = async (data: DocumentForm) => {
    const files = data.file ?? []
    onBusyChange(true)
    form.clearErrors('root')
    const failed: UploadFileStatus[] = []
    try {
      for (const file of files) {
        try {
          await uploadDocument(traderId, file, {
            type: isFinancialStatement ? 'financial_statement' : 'other',
            notes: data.notes ?? '',
            fileOn: destinationOf(data)
          })
        } catch (err: unknown) {
          failed.push({ file, status: 'error', error: isError(err) ? err.message : 'Could not upload the document.' })
        }
      }
      await utils.invalidate()
    } finally {
      onBusyChange(false)
    }
    if (failed.length > 0) {
      form.setValue(
        'file',
        failed.map((row) => row.file)
      )
      form.setError('root', { type: 'manual', message: failedUploadsMessage(failed, files.length) })
      return
    }
    toast({ title: files.length === 1 ? 'Document uploaded' : `${files.length} documents uploaded` })
    onDone()
  }

  return <TraderDocumentForm form={form} onSubmit={onSubmit} onCancel={onDone} isSubmitting={busy} />
}
